// apps/web/src/features/admin/automations/RouteConfigBody.tsx
interface Props {
  effectKey: string;
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /** The job-type's pipeline steps. The route_to_queue target picker uses these
   *  (option value = stepType) so the stored `targetStep` resolves against the
   *  runtime consumer's `pipeline.find(s => s.stepType === targetStep)` in
   *  workflow-engine.ts — restoring the old editor's step-targeting (#9). */
  pipelineSteps: { stepType: string; label: string }[];
  /** The tenant's REAL roles. The notify picker writes the stable `slug` (so a
   *  later rename can't orphan the rule — see role-slug.ts) and displays `name`.
   *  A stored legacy NAME still resolves to its role for display. */
  roles?: { slug: string; name: string }[];
  /** The tenant's active users. The notify target-user picker writes the
   *  user's id, displayed as "Name (email)". A stored id matching no current
   *  user stays selectable — same stale-value posture as the role picker
   *  (a publish gate, `notify.targetUserId.unknown`, surfaces it, not silent
   *  loss). */
  users?: { id: string; name: string; email: string }[];
  /** The form's field names — the set_field "Stamp a value" target picker offers
   *  these (same source the check-family fieldRef picker uses). The stored value
   *  resolves against the executor's `fieldDefinition.internalName` lookup. */
  fieldNames?: string[];
  /** The job's form classification keys — the reclassify "direct" target picker
   *  offers these (P5 T6). When absent, reclassify falls back to a free-text key
   *  input. The stored value resolves against `document.classification`. */
  classifications?: { key: string; label: string }[];
}

const s = (v: unknown): string => (typeof v === 'string' ? v : '');
const SET_FIELD_MODES: { value: string; label: string }[] = [
  { value: 'replace', label: 'Replace' },
  { value: 'append', label: 'Append' },
  { value: 'fill_if_empty', label: 'Only when empty' },
];

/** Per-route config inputs. route/notify write config keys; hold/reject/escalate/notify
 *  carry their text on config.message (v1); skip_step/reclassify are parameterless. */
export function RouteConfigBody({ effectKey, config, onChange, pipelineSteps, fieldNames = [], classifications = [], roles = [], users = [] }: Props) {
  const patch = (p: Record<string, unknown>) => onChange({ ...config, ...p });

  const reasonRow = (label: string) => (
    <label className="automation-editor__row">
      <span>{label}</span>
      <input data-testid="route-reason" type="text" value={s(config.message)}
        onChange={(e) => patch({ message: e.target.value })} />
    </label>
  );

  switch (effectKey) {
    case 'route_to_queue':
      return (
        <div className="automation-route-config">
          <label className="automation-editor__row">
            <span>Route to step</span>
            <select data-testid="route-target-step" value={s(config.targetStep)}
              onChange={(e) => patch({ targetStep: e.target.value })}>
              <option value="">— pick a step —</option>
              {pipelineSteps.map((st) => <option key={st.stepType} value={st.stepType}>{st.label}</option>)}
            </select>
          </label>
        </div>
      );
    case 'notify': {
      const current = s(config.targetRole);
      const currentUserId = s(config.targetUserId);
      // Resolve the stored value — a stable slug (new) or a legacy name — to the
      // role it names, so the picker shows the right option selected either way.
      const matched = roles.find((r) => r.slug === current || r.name === current);
      const selectValue = matched ? matched.slug : current;
      return (
        <div className="automation-route-config">
          {/* F-057 — the runtime treats targetRole and targetUserId as a UNION
              (workflow-engine.ts B1/B2: one Notification visible to the role's
              members AND the user; email legs unioned), so each leg is
              independently clearable via its × — clearing drops the key
              (undefined disappears on save), never writes "". */}
          <div className="automation-editor__row">
            <span>Notify role</span>
            {roles.length > 0 ? (
              // Option value is the role's stable SLUG (rename-safe); the label is
              // its display name. Picking a role migrates a legacy name-value to
              // the slug on save. A stored value naming no current role stays
              // selectable so the publish gate (notify.targetRole.unknown), not
              // silent loss, surfaces it.
              <select data-testid="route-notify-role" aria-label="Notify role" value={selectValue}
                onChange={(e) => patch({ targetRole: e.target.value })}>
                <option value="" disabled>Choose a role…</option>
                {roles.map((r) => <option key={r.slug} value={r.slug}>{r.name}</option>)}
                {current && !matched && (
                  <option value={current}>{current}</option>
                )}
              </select>
            ) : (
              <input data-testid="route-notify-role-text" aria-label="Notify role" type="text" value={current}
                placeholder="Role name (exact)"
                onChange={(e) => patch({ targetRole: e.target.value })} />
            )}
            {current && (
              <button type="button" className="automation-editor__clear"
                data-testid="route-notify-role-clear" aria-label="Clear role"
                title="Clear role"
                onClick={() => patch({ targetRole: undefined })}>
                &times;
              </button>
            )}
          </div>
          <div className="automation-editor__row">
            <span>Notify user</span>
            <select data-testid="route-notify-user" aria-label="Notify user" value={currentUserId}
              // B1 — re-selecting "— no specific user —" must REMOVE the key,
              // not store "". A blank targetUserId used to null out targetRole
              // too (see workflow-engine.ts) and the rule notified nobody.
              // undefined drops the key on save (JSON.stringify).
              onChange={(e) => patch({ targetUserId: e.target.value || undefined })}>
              <option value="">— no specific user —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
              {currentUserId && !users.some((u) => u.id === currentUserId) && (
                <option value={currentUserId}>{currentUserId}</option>
              )}
            </select>
            {currentUserId && (
              <button type="button" className="automation-editor__clear"
                data-testid="route-notify-user-clear" aria-label="Clear user"
                title="Clear user"
                onClick={() => patch({ targetUserId: undefined })}>
                &times;
              </button>
            )}
          </div>
          <p className="automation-editor__note" data-testid="notify-audience-note">
            Notifies everyone in the role and the named user — pick either or both.
          </p>
          <label className="automation-editor__row">
            <span>Also send email</span>
            <input data-testid="route-notify-send-email" type="checkbox"
              checked={config.sendEmail === true}
              onChange={(e) => patch({ sendEmail: e.target.checked })} />
          </label>
          {reasonRow('Message')}
        </div>
      );
    }
    case 'hold': return <div className="automation-route-config">{reasonRow('Reason')}</div>;
    case 'reject': return <div className="automation-route-config">{reasonRow('Reason')}</div>;
    case 'escalate':
      return (
        <div className="automation-route-config">
          {reasonRow('Reason')}
          <p className="automation-editor__note">Escalates to the stage&apos;s supervisor.</p>
        </div>
      );
    case 'set_field': {
      const mode = s(config.mode) || 'replace';
      return (
        <div className="automation-route-config">
          <label className="automation-editor__row">
            <span>Target field</span>
            <select data-testid="set-field-target" value={s(config.targetField)}
              onChange={(e) => patch({ targetField: e.target.value })}>
              <option value="">— pick a field —</option>
              {fieldNames.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label className="automation-editor__row">
            <span>Value</span>
            <input data-testid="set-field-value" type="text" value={s(config.value)}
              onChange={(e) => patch({ value: e.target.value })} />
          </label>
          <label className="automation-editor__row">
            <span>Value is</span>
            <select data-testid="set-field-value-kind" value={s(config.valueKind) || 'literal'}
              onChange={(e) => patch({ valueKind: e.target.value })}>
              <option value="literal">Literal text</option>
              <option value="expression">Expression</option>
            </select>
          </label>
          <label className="automation-editor__row">
            <span>Mode</span>
            <select data-testid="set-field-mode" value={mode}
              onChange={(e) => patch({ mode: e.target.value })}>
              {SET_FIELD_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </label>
          {mode === 'append' && (
            <label className="automation-editor__row">
              <span>Delimiter</span>
              <input data-testid="set-field-delimiter" type="text" value={s(config.delimiter) || ','}
                onChange={(e) => patch({ delimiter: e.target.value })} />
            </label>
          )}
        </div>
      );
    }
    case 'reprioritize': {
      const raw = config.priority;
      const priority = typeof raw === 'number' && Number.isFinite(raw) ? String(raw) : '';
      return (
        <div className="automation-route-config">
          <label className="automation-editor__row">
            <span>Priority</span>
            <input data-testid="reprioritize-priority" type="number" step={1} value={priority}
              onChange={(e) => {
                const num = Number(e.target.value);
                patch({ priority: e.target.value !== '' && Number.isFinite(num) ? Math.trunc(num) : undefined });
              }} />
          </label>
          <p className="automation-editor__note">Higher numbers sort to the front of the queue.</p>
        </div>
      );
    }
    case 'reclassify':
      return (
        <div className="automation-route-config">
          <label className="automation-editor__row">
            <span>Set classification to</span>
            {classifications.length > 0 ? (
              <select data-testid="reclassify-target-classification" value={s(config.targetClassification)}
                onChange={(e) => patch({ targetClassification: e.target.value || undefined })}>
                <option value="">— send back to Classify —</option>
                {classifications.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            ) : (
              <input data-testid="reclassify-target-classification" type="text" value={s(config.targetClassification)}
                placeholder="(send back to Classify)"
                onChange={(e) => patch({ targetClassification: e.target.value || undefined })} />
            )}
          </label>
          <p className="automation-editor__note" data-testid="route-note">
            Leave empty to send the document back to the Classify step; pick a classification to set it directly and keep advancing.
          </p>
        </div>
      );
    case 'skip_step':
    default:
      return (
        <div className="automation-route-config">
          <label className="automation-editor__row">
            <span>Only skip step</span>
            <select data-testid="skip-target-step" value={s(config.targetStep)}
              onChange={(e) => patch({ targetStep: e.target.value || undefined })}>
              <option value="">— the next step —</option>
              {pipelineSteps.map((st) => <option key={st.stepType} value={st.stepType}>{st.label}</option>)}
            </select>
          </label>
          <p className="automation-editor__note" data-testid="route-note">
            Skips the next pipeline step. Pick a specific step to skip it only when it is next.
          </p>
        </div>
      );
  }
}
